import { ExcelExportParams, GridApi } from 'ag-grid-community';
import React, { useContext } from 'react';

/*
    fileName: Name of file to be exported
    sheetName: Name of the sheet
*/
export type ExcelExportConfig = {
    fileName: string;
    sheetName: string;
};

/*
    sheets: sheets to be exported
    sheetOrder: list of sheet names to define export order
*/
export type ExcelExport = {
    sheets: GridApiConfig[];
    sheetOrder: string[];
};

/*
    sheetName: name of the sheet
    gridApi: gridApi reference for a table
*/
type GridApiConfig = {
    sheetName: string;
    grid: GridApi<unknown>;
    exportOptions: ExcelExportParams;
};

/* This Context references Excel file to export through Budget Module, and references GridApis to be exported as sheets of the excel file. */
type GridApiState = [
    value: { [fileName: string]: ExcelExport },
    setValue: React.Dispatch<
        React.SetStateAction<{ [fileName: string]: ExcelExport }>
    >,
];

const GridApiContext = React.createContext<GridApiState>([{}, () => {}]);

export default GridApiContext;

/* Add a gridApi to context. */
export const registerGridApi = (
    state: GridApiState,
    fileName: string,
    gridConfig: GridApiConfig,
) => {
    const [value, setValue] = state;
    const newValue = value[fileName];
    if (newValue.sheets !== undefined) {
        // fixme: we should not try to add the same sheet twice
        // fixme: this is a workaround to avoid adding the same sheet twice
        // fixme: the reason of this is that we mount the component twice
        const sheetExists = newValue.sheets.find(
            (s) => s.sheetName === gridConfig.sheetName,
        );
        if (sheetExists) {
            newValue.sheets.splice(newValue.sheets.indexOf(sheetExists), 1);
        }
        newValue.sheets.push(gridConfig);
    } else {
        newValue.sheets = [gridConfig];
    }
    setValue({ ...value, [fileName]: newValue });
};

/* Remove grid from context, based on its fileName and sheetName. */
export const unregisterGrid = (
    state: GridApiState,
    excelExportConfig: ExcelExportConfig,
) => {
    const [value, setValue] = state;
    const registeredGridsForExcelFile = value[excelExportConfig.fileName];
    if (registeredGridsForExcelFile.sheets === undefined) {
        return;
    }
    registeredGridsForExcelFile.sheets =
        registeredGridsForExcelFile.sheets.filter(
            (config) => config.sheetName !== excelExportConfig.sheetName,
        );
    setValue({
        ...value,
        [excelExportConfig.fileName]: registeredGridsForExcelFile,
    });
};

export const useGridApiContext = () => useContext(GridApiContext);
